import apiClient from './apiClient';
import tokenStorage from './tokenStorage';
import { asAxiosError } from './apiError';
import { logAudit } from './auditLogger';
import { generateTraceId } from './trace';

export interface TierInfo {
  plan: string;
  // is_paid reflects the DISPLAY plan: an admin using the "view as plan"
  // preview flips it (see X-View-As-Plan). real_is_paid is always the user's
  // true account plan, so the client can keep labelling/tracking the real tier
  // while previewing. Web-only; the mobile app never sends the preview header.
  is_paid: boolean;
  real_is_paid?: boolean;
  trial_active: boolean;
  trial_ends_at: string | null;
  daily_chapters_remaining: number | null; // null for paid users (unlimited)
  daily_free_limit?: number; // backend-owned free-tier cap, for client copy
  chapters_read_today: number;
  /** When the current Plus period ends; null/absent for free accounts. */
  plus_ends_at?: string | null;
  /** True when Plus renews on that date rather than lapsing. */
  plus_renews?: boolean;
  /** True when the Plus in force was bought on the website, so it is managed there, not in a store. */
  plus_web?: boolean;
}

export interface LinkedIdentity {
  provider: string;
  uid?: string;
  id?: string;
  email?: string;
}

export interface User {
  id: number;
  email: string;
  name?: string;
  role: string;
  admin: boolean;
  /** Can use the author portal. True for admins too. */
  author?: boolean;
  /** Can use the reviewer portal. True for admins too. */
  reviewer?: boolean;
  paid?: boolean;
  plan?: string;
  provider?: string;
  trial_ends_at?: string;
  linked_providers?: string[];
  identities?: LinkedIdentity[];
  tier_info?: TierInfo;
  /** No chosen and no assessed level yet: show the level step once. */
  needs_level_pick?: boolean;
  notification_prefs?: {
    nudges?: boolean;
    offers_and_new_content?: boolean;
    nudge_every_days?: 1 | 3 | 7;
  };
}

export interface AuthResponse {
  user: User;
  message?: string;
}

/**
 * Platform-neutral auth service shared by the web and React Native apps.
 *
 * Contains only the email/password + session/profile methods, which depend on
 * nothing beyond the (env-agnostic) core apiClient, tokenStorage, and logging.
 * The web-redirect OAuth methods (signInWithOAuth / handleOAuthCallback) live in
 * the web subclass (frontend/src/services/AuthService.ts); React Native will add
 * its own native OAuth in Phase 4. This class MUST NOT import runtimeEnv or touch
 * window/document.
 */
export class CoreAuthService {
  // Get current user — returns null for 401 (not authenticated), re-throws network/server errors
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await apiClient.get('/api/current_user');
      return response.data.user;
    } catch (error) {
      const status = asAxiosError(error).response?.status;
      if (status === 401 || status === 403) return null;
      throw error;
    }
  }

  // Sign up with email/password
  async signUp(email: string, password: string, name?: string, honeypot?: string, termsAccepted?: boolean): Promise<User> {
    const traceId = generateTraceId();
    logAudit('sign_up_attempt', { email }, traceId);

    const response = await apiClient.post('/api/sign_up', {
      user: {
        email,
        password,
        password_confirmation: password,
        name,
        honeypot,
        terms_accepted: termsAccepted,
      },
    }, { headers: { 'X-Client-Trace-Id': traceId } });

    // JWT token is automatically stored via axios interceptor
    return response.data.user;
  }

  // Sign in with email/password
  async signIn(email: string, password: string, honeypot?: string): Promise<User> {
    const traceId = generateTraceId();
    logAudit('sign_in_attempt', { email }, traceId);

    const response = await apiClient.post('/api/sign_in', {
      user: {
        email,
        password,
        honeypot,
      },
    }, { headers: { 'X-Client-Trace-Id': traceId } });

    // JWT token is automatically stored via axios interceptor
    return response.data.user;
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      await apiClient.delete('/api/sign_out');
    } catch {
      // Best-effort server-side revocation — token is cleared locally regardless
    } finally {
      tokenStorage.clearTokens();
    }
  }

  // Request password reset
  async requestPasswordReset(email: string, honeypot?: string): Promise<{ message: string }> {
    const traceId = generateTraceId();
    logAudit('password_reset_request', { email }, traceId);

    const response = await apiClient.post('/api/password', {
      user: { email, honeypot },
    }, { headers: { 'X-Client-Trace-Id': traceId } });
    return response.data;
  }

  // Reset password with token
  async resetPassword(token: string, password: string, honeypot?: string): Promise<{ message: string }> {
    const traceId = generateTraceId();
    logAudit('password_reset_submit', { hasToken: !!token }, traceId);

    const response = await apiClient.put('/api/password', {
      user: {
        reset_password_token: token,
        password,
        password_confirmation: password,
        honeypot,
      },
    }, { headers: { 'X-Client-Trace-Id': traceId } });
    return response.data;
  }

  // Accept waitlist invitation — sets password and activates the account
  async acceptInvite(email: string, inviteToken: string, password: string, name?: string): Promise<{ message: string }> {
    const traceId = generateTraceId();
    logAudit('invite_accept', { email }, traceId);

    // Backend PasswordsController#update routes on a top-level invite_token param
    // (not nested under user:) to handle_invite_signup
    const response = await apiClient.put('/api/password', {
      email,
      invite_token: inviteToken,
      password,
      password_confirmation: password,
      name,
    }, { headers: { 'X-Client-Trace-Id': traceId } });
    return response.data;
  }

  // Resend confirmation email
  async resendConfirmation(email: string): Promise<{ message: string }> {
    const response = await apiClient.post('/api/confirmation', {
      user: { email },
    });
    return response.data;
  }

  // Unlink authentication provider (requires user to be authenticated)
  async unlinkProvider(provider: string): Promise<{ message: string; user: User }> {
    try {
      const response = await apiClient.delete(`/api/profile/unlink_provider/${provider}`);
      return response.data;
    } catch (error: unknown) {
      throw new Error(asAxiosError(error).response?.data?.error || 'Failed to unlink provider');
    }
  }

  // Get app settings (login/signup/waitlist enabled status) - public endpoint
  async getAppSettings(): Promise<{
    login_enabled: boolean;
    signup_enabled: boolean;
    waitlist_enabled: boolean;
    auth_providers: string[];
    /** False until the stores + RevenueCat are configured — all upgrade/paywall UI hides. */
    purchases_enabled: boolean;
    /** Backend-owned free-tier numbers for client copy (never hardcode). */
    daily_free_limit: number;
    free_preview_chapters: number;
    /** Closed-beta mode: clients surface "join our Discord" instead of open signup. */
    discord_beta_enforcement: boolean;
    discord_invite_url: string | null;
    beta_ios_download_url: string | null;
    beta_android_download_url: string | null;
    beta_android_testers_url: string | null;
  } | null> {
    try {
      const response = await apiClient.get('/api/app_settings');
      // Validate the response is actual JSON from the backend,
      // not the SPA fallback returning index.html
      if (typeof response.data !== 'object' || response.data === null) {
        return null;
      }
      return {
        login_enabled: response.data.login_enabled === true,
        signup_enabled: response.data.signup_enabled === true,
        waitlist_enabled: response.data.waitlist_enabled === true,
        auth_providers: response.data.auth_providers ?? [],
        purchases_enabled: response.data.purchases_enabled === true,
        daily_free_limit: typeof response.data.daily_free_limit === 'number' ? response.data.daily_free_limit : 3,
        free_preview_chapters:
          typeof response.data.free_preview_chapters === 'number' ? response.data.free_preview_chapters : 3,
        discord_beta_enforcement: response.data.discord_beta_enforcement === true,
        discord_invite_url: typeof response.data.discord_invite_url === 'string' ? response.data.discord_invite_url : null,
        beta_ios_download_url:
          typeof response.data.beta_ios_download_url === 'string' ? response.data.beta_ios_download_url : null,
        beta_android_download_url:
          typeof response.data.beta_android_download_url === 'string' ? response.data.beta_android_download_url : null,
        beta_android_testers_url:
          typeof response.data.beta_android_testers_url === 'string' ? response.data.beta_android_testers_url : null,
      };
    } catch (error: unknown) {
      console.error('Failed to fetch app settings:', error);
      // Return null on error, pages will use defaults
      return null;
    }
  }
}

export const coreAuthService = new CoreAuthService();
